// store/modules/properties.js
import { API_URL } from "@/mainconfig"; // Import API_URL from mainconfig.js
import { getResponseMessage } from "@/utils/responseMapping"; // Import error message mapping
import i18n from "@/plugins/LanguageComponent.js"; // Import i18n for translations
import { createLatestRequest } from "@/utils/latestRequest.mjs";

const pickDisplayName = (item) =>
    item?.DisplayName || item?.Name || null;

const normalizePropertyEntity = (property) => {
    if (!property || typeof property !== "object") return property;
    return {
        ...property,
        Name: pickDisplayName(property) || property.Name,
    };
};

const pickAvailabilityName = (item = {}, room, roomType) => {
    const isPriceByRoomType = Boolean(
        item?.IsRoomType === true ||
            (item?.RoomTypeId != null && item?.RoomId == null)
    );
    const roomName = pickDisplayName(room) || null;
    const roomTypeName = pickDisplayName(roomType) || null;
    const fallbackName = item?.Name || null;

    return isPriceByRoomType
        ? roomTypeName || roomName || fallbackName
        : roomName || roomTypeName || fallbackName;
};

const normalizeAvailabilityItem = (item = {}) => {
    const room = item.Room
        ? {
              ...item.Room,
              Name: pickDisplayName(item.Room) || item.Room.Name,
          }
        : item.Room;

    const roomType = item.RoomType
        ? {
              ...item.RoomType,
              Name: pickDisplayName(item.RoomType) || item.RoomType.Name,
          }
        : item.RoomType;

    const propertyFromRoom = room?.Property
        ? normalizePropertyEntity(room.Property)
        : null;
    const propertyFromRoomType = roomType?.Property
        ? normalizePropertyEntity(roomType.Property)
        : null;

    return {
        ...item,
        Name: pickAvailabilityName(item, room, roomType),
        PropertyName:
            propertyFromRoom?.Name ||
            propertyFromRoomType?.Name ||
            item.PropertyName ||
            null,
        Room: propertyFromRoom ? { ...room, Property: propertyFromRoom } : room,
        RoomType: propertyFromRoomType
            ? { ...roomType, Property: propertyFromRoomType }
            : roomType,
    };
};

const normalizeAvailabilityPayload = (payload = {}) => {
    if (!payload || typeof payload !== "object") return payload;
    const rows = Array.isArray(payload.data) ? payload.data : [];
    return {
        ...payload,
        data: rows.map(normalizeAvailabilityItem),
    };
};

const normalizeRoomDetailsPayload = (payload = {}) => {
    if (!payload || typeof payload !== "object") return payload;
    const data = payload.data && typeof payload.data === "object" ? payload.data : null;
    if (!data) return payload;

    const room = data.Room
        ? {
              ...data.Room,
              Name: pickDisplayName(data.Room) || data.Room.Name,
          }
        : data.Room;
    const roomType = data.RoomType
        ? {
              ...data.RoomType,
              Name: pickDisplayName(data.RoomType) || data.RoomType.Name,
          }
        : data.RoomType;
    const propertyFromRoom = normalizePropertyEntity(room?.Property);
    const propertyFromRoomType = normalizePropertyEntity(roomType?.Property);
    const property = normalizePropertyEntity(data.Property);

    return {
        ...payload,
        data: {
            ...data,
            PropertyName:
                propertyFromRoom?.Name ||
                propertyFromRoomType?.Name ||
                property?.Name ||
                data.PropertyName ||
                null,
            Room: room
                ? {
                      ...room,
                      Property: propertyFromRoom || room.Property,
                  }
                : room,
            RoomType: roomType
                ? {
                      ...roomType,
                      Property: propertyFromRoomType || roomType.Property,
                  }
                : roomType,
            Property: property || data.Property,
            Name: data.Name || pickAvailabilityName(data, room, roomType),
        },
    };
};

const state = {
    searchPayload: JSON.parse(localStorage.getItem("searchPayload")) || null, //untuk halaman home ke map
    properties: [],
    displayedProperties: [],
    lastSearchQuery:
        JSON.parse(localStorage.getItem("lastSearchQuery")) || null, //untuk halaman property detail
    roomAvailability: {
        data: [],
        loading: false,
        error: null,
        errorKey: null,
        errorMessage: null,
    },
    roomDetails: {
        data: JSON.parse(sessionStorage.getItem("roomDetails")) || {}, // Ambil dari sessionStorage jika ada
        loading: false,
        error: null,
        errorKey: null,
        errorMessage: null,
    },
    categories: [
        { label: "All", value: "all" },
        { label: "Hotel", value: "Hotel" },
        { label: "Guest House", value: "Guest House" },
    ],
    selectedCategory: "all", // Default kategori
    categoryProperties: [], // Properti berdasarkan kategori
    isCategoryLoading: false,
    recommendedProperties: [],
    isRecommendedLoading: false,
    error: null,
    errorKey: null,
    errorMessage: null,
    loadCount: 10,
    selectedBudgets: {
        economy: false,
        popular: false,
        luxury: false,
    },
    isFetchingRoomDetails: false,
    //=============================
    countries: [],
    isCountriesLoading: false,
    countriesError: null,
    //=============================
    provinces: [],
    isProvincesLoading: false,
    provincesError: null,
    //=============================
    cities: [],
    isCitiesLoading: false,
    citiesError: null,
    isOccupancyDateLoading: false,
};

// Penjaga "hanya respons terbaru yang menang" untuk getRoomDetails (lihat action-nya).
// Sengaja di luar state: giliran request transien, bukan data yang perlu reaktif.
const roomDetailsGate = createLatestRequest();

const getters = {
    getError: (state) => state.errorMessage,
    properties: (state) => state.properties,
    displayedProperties: (state) => state.displayedProperties,
    selectedBudgets: (state) => state.selectedBudgets,
    locations(state) {
        // const locationsSet = new Set(state.properties.map((prop) => prop.location));
        // return Array.from(locationsSet).map((loc) => ({
        //   label: loc,
        //   value: loc,
        // }));
        return state.properties.map((prop) => ({
            label: prop.cityName, // atau nama kota
            value: prop.cityId, // gunakan cityId
        }));
    },
    getSearchPayload: (state) => state.searchPayload,
    getRoomDetailsData: (state) => state.roomDetails.data,

    // Getters for Room Availability
    isRoomAvailabilityLoading: (state) => state.roomAvailability.loading,
    getRoomAvailabilityError: (state) => state.roomAvailability.errorMessage,
    roomAvailabilityData: (state) => state.roomAvailability.data,
    // Getters for Room Details
    isRoomDetailsLoading: (state) => state.roomDetails.loading,
    getRoomDetailsError: (state) => state.roomDetails.errorMessage,
    roomDetailsData: (state) => state.roomDetails.data,
    getLastSearchQuery: (state) => state.lastSearchQuery,

    //CategorySection
    getCategories: (state) => state.categories,
    getSelectedCategory: (state) => state.selectedCategory,
    getCategoryProperties: (state) => state.categoryProperties,
    // Filter tipe properti dikerjakan backend (param propertyTypeId di setCategory).
    // Dulu difilter di sini atas hasil "10 terbaru tanpa filter" → chip apa pun
    // hampir selalu berujung grid kosong walau API mengirim data.
    getFilteredCategoryProperties: (state) => state.categoryProperties,
    isCategoryLoading: (state) => state.isCategoryLoading,
    getRecommendedProperties: (state) => state.recommendedProperties,
    isRecommendedLoading: (state) => state.isRecommendedLoading,

    getCountries: (state) => state.countries,
    isCountriesLoading: (state) => state.isCountriesLoading,
    getCountriesError: (state) => state.countriesError,

    getProvinces: (state) => state.provinces,
    isProvincesLoading: (state) => state.isProvincesLoading,
    getProvincesError: (state) => state.provincesError,

    getCities: (state) => state.cities,
    isCitiesLoading: (state) => state.isCitiesLoading,
    getCitiesError: (state) => state.citiesError,
};

const actions = {
    // Existing Actions
    async fetchProperties({ commit }) {
        try {
            const fullURL = `${process.env.VUE_APP_BASE_URL}/properties`;
            console.log("Fetching properties from:", fullURL); // Log the full URL
            const response = await API_URL.get("/properties"); // Replace with your actual API endpoint
            commit("setProperties", response.data);
            commit("setError", null);
        } catch (error) {
            console.error("Error fetching properties:", error); // Log the error
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            // Optionally, handle specific status codes differently
        } finally {
            console.log("Berhasil");
        }
    },
    async fetchRoomAvailability({ commit }, payload) {
        commit("setRoomAvailabilityLoading", true);
        try {
            const response = await API_URL.get("/roomAvailability/check-v2", {
                params: payload,
            });
            commit("setRoomAvailabilityData", response.data);
            commit("setRoomAvailabilityError", null);
            return response.data;
        } catch (error) {
            console.error("Error fetching room availability:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit(
                "setRoomAvailabilityError",
                errorKey || "errors.defaultError"
            );
        } finally {
            commit("setRoomAvailabilityLoading", false);
        }
    },
    async roomAvailability({ dispatch }, payload) {
        return dispatch("fetchRoomAvailability", payload);
    },
    loadMore({ commit }) {
        commit("loadMoreProperties");
    },

    toggleBudgetSelection({ commit }, budgetType) {
        commit("toggleBudgetSelection", budgetType);
    },

    applyBudgetFilter({ commit }, filterData) {
        commit("applyBudgetFilter", filterData);
    },

    clearError({ commit }) {
        commit("clearError");
    },

    //Khusus perubahan value didalam halaman Detail
    // Pindah tab periode (Harian/Bulanan/Tahunan) cepat = beberapa request beruntun.
    // Dulu request BARU dibuang selama ada yang jalan -> respons tab lama yang mengisi
    // state, harga nyangkut di periode sebelumnya. Sekarang: request lama dibatalkan
    // (AbortController) dan hanya respons TERBARU yang boleh menyentuh state.
    // Request usang mengembalikan null -> pemanggil wajib `if (!data) return;`.
    async getRoomDetails({ commit }, payload) {
        const turn = roomDetailsGate.start();

        // console.log("getRoomDetails action called with payload:", payload);
        commit("setRoomDetailsLoading", true);
        commit("setFetchingRoomDetails", true); // Set flag ke true
        try {
            const response = await API_URL.get("/roomAvailability/detail-v2", {
                params: payload,
                signal: turn.signal,
            });

            if (!turn.isLatest()) return null; // sudah disusul request tab lain
            commit("setRoomDetailsData", response.data);
            commit("setRoomDetailsError", null);
            return response.data;
        } catch (error) {
            // Request yang dibatalkan selalu bukan yang terbaru -> diam, jangan tulis error
            // (tanpa ini pemanggil melempar user ke Home tiap kali tab diganti cepat).
            if (!turn.isLatest()) return null;
            console.error("Error fetching room details:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setRoomDetailsError", errorKey || "errors.defaultError");
            throw error;
        } finally {
            if (turn.isLatest()) {
                commit("setRoomDetailsLoading", false);
                commit("setFetchingRoomDetails", false); // Set flag kembali ke false
            }
        }
    },
    async getAvailabilityWindow(_context, payload) {
        const params = {};
        const roomId = Number(payload?.roomId ?? payload?.RoomId ?? payload ?? 0);
        const roomTypeId = Number(payload?.roomTypeId ?? payload?.RoomTypeId ?? 0);
        const propertyId = Number(payload?.propertyId ?? payload?.PropertyId ?? 0);

        if (Number.isInteger(roomId) && roomId > 0) {
            params.RoomId = roomId;
        } else if (Number.isInteger(roomTypeId) && roomTypeId > 0) {
            params.RoomTypeId = roomTypeId;
        } else if (Number.isInteger(propertyId) && propertyId > 0) {
            params.PropertyId = propertyId;
        } else {
            throw new Error("RoomId/RoomTypeId/PropertyId is required");
        }

        const response = await API_URL.get("/availability-window/setting/effective", {
            params,
        });
        return response.data;
    },
    async validateBookingRequestLink(_context, token) {
        if (!token) return null;
        const response = await API_URL.get("/booking-request/validate-link", {
            params: { t: token },
        });
        return response.data;
    },
    async submitBookingRequest(_context, payload) {
        const response = await API_URL.post("/booking-request", payload);
        return response.data;
    },

    async fetchCategoryProperties({ commit }, params) {
        commit("setCategoryLoading", true);
        try {
            const response = await API_URL.get("/roomAvailability/check-v2", {
                params,
            });
            // Simpan semua data ke state
            commit("setCategoryProperties", response.data.data || []);
        } catch (error) {
            console.error("Error fetching category properties:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit(
                "setRoomAvailabilityError",
                errorKey || "errors.defaultError"
            );
        } finally {
            commit("setCategoryLoading", false);
        }
    },

    setCategory({ commit, dispatch }, category) {
        commit("setSelectedCategory", category);
        dispatch("fetchCategoryProperties", {
            page: 1,
            limit: 10,
            isLatest: true,
            ...(category !== "all" ? { propertyTypeId: category } : {}),
        });
    },

    async fetchRecommendedProperties({ commit }) {
        commit("setRecommendedLoading", true);
        try {
            const response = await API_URL.get("/roomAvailability/check-v2", {
                params: {
                    page: 1,
                    limit: 10,
                    isRecommended: true,
                },
            });
            commit("setRecommendedProperties", response.data.data || []);
        } catch (error) {
            console.error("Error fetching recommended properties:", error);
        } finally {
            commit("setRecommendedLoading", false);
        }
    },

    async fetchCountries({ commit }) {
        commit("setCountriesLoading", true);
        try {
            const response = await API_URL.get("/region/getCountries");
            // console.log('✅ Fetch Countries Response:', response.data);

            commit("setCountries", response.data.data);
            commit("setCountriesError", null);
        } catch (error) {
            console.error("Error fetching countries:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setCountriesError", errorKey || "errors.defaultError");
        } finally {
            commit("setCountriesLoading", false);
        }
    },

    async fetchProvinces({ commit }, countryId) {
        commit("setProvincesLoading", true);
        try {
            const response = await API_URL.get(
                `/region/getProvinces/${countryId}`
            );
            // console.log('✅ Fetch Provinces Response:', response.data);

            commit("setProvinces", response.data.data);
            commit("setProvincesError", null);
        } catch (error) {
            console.error("❌ Error fetching provinces:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setProvincesError", errorKey || "errors.defaultError");
        } finally {
            commit("setProvincesLoading", false);
        }
    },

    async fetchCities({ commit }, provinceId) {
        commit("setCitiesLoading", true);
        try {
            const response = await API_URL.get(
                `/region/getCities/${provinceId}`
            );
            // console.log('✅ Fetch Cities Response:', response.data);

            commit("setCities", response.data.data);
            commit("setCitiesError", null);
        } catch (error) {
            console.error("❌ Error fetching cities:", error);
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setCitiesError", errorKey || "errors.defaultError");
        } finally {
            commit("setCitiesLoading", false);
        }
    },

    async getOccupancyDate({ commit }, payload) {
        try {
            const response = await API_URL.get(
                "/roomAvailability/getOccupancyDate",
                {
                    params: payload,
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching getOccupancyDate:", error);
            throw error;
        } finally {
            commit("setOccupancyDateLoading", false);
        }
    },
};

const mutations = {
    // Existing Mutations
    setLastSearchQuery(state, query) {
        state.lastSearchQuery = query;

        localStorage.setItem("lastSearchQuery", JSON.stringify(query));
    },
    clearLastSearchQuery(state) {
        state.lastSearchQuery = {}; // Reset ke object kosong
        localStorage.removeItem("lastSearchQuery"); // Hapus data dari localStorage
    },
    setProperties(state, properties) {
        state.properties = properties;
        state.displayedProperties = properties.slice(0, state.loadCount);
    },
    loadMoreProperties(state) {
        const newCount = state.displayedProperties.length + 2;
        state.displayedProperties = state.properties.slice(0, newCount);
    },
    toggleBudgetSelection(state, budgetType) {
        if (
            Object.prototype.hasOwnProperty.call(
                state.selectedBudgets,
                budgetType
            )
        ) {
            state.selectedBudgets[budgetType] =
                !state.selectedBudgets[budgetType];
        }
    },
    applyBudgetFilter(state, filterData) {
        const { location, priceMin, priceMax, budgetOptions } = filterData;
        let filtered = state.properties;

        if (location) {
            filtered = filtered.filter((prop) => prop.location === location);
        }

        if (priceMin) {
            filtered = filtered.filter(
                (prop) => prop.price >= Number(priceMin)
            );
        }

        if (priceMax) {
            filtered = filtered.filter(
                (prop) => prop.price <= Number(priceMax)
            );
        }

        if (budgetOptions) {
            const budgetFilters = [];
            if (budgetOptions.economy) {
                budgetFilters.push((prop) => prop.price < 4500000);
            }
            if (budgetOptions.popular) {
                budgetFilters.push(
                    (prop) => prop.price >= 4500000 && prop.price <= 7000000
                );
            }
            if (budgetOptions.luxury) {
                budgetFilters.push((prop) => prop.price > 7000000);
            }
            if (budgetFilters.length > 0) {
                filtered = filtered.filter((prop) =>
                    budgetFilters.some((filterFn) => filterFn(prop))
                );
            }
        }

        state.displayedProperties = filtered.slice(
            0,
            state.displayedProperties.length
        );
    },
    setError(state, errorKey) {
        state.errorKey = errorKey;
        state.errorMessage = errorKey ? i18n.global.t(errorKey) : null;
    },
    clearError(state) {
        state.errorKey = null;
        state.errorMessage = null;
    },

    setSearchPayload(state, payload) {
        state.searchPayload = payload;
        localStorage.setItem("searchPayload", JSON.stringify(payload));
    },
    // New Mutations for Room Availability

    setRoomAvailabilityLoading(state, isLoading) {
        state.roomAvailability.loading = isLoading;
    },
    setRoomAvailabilityData(state, data) {
        state.roomAvailability.data = normalizeAvailabilityPayload(data);
    },
    setRoomAvailabilityError(state, errorKey) {
        state.roomAvailability.errorKey = errorKey;
        state.roomAvailability.errorMessage = errorKey
            ? i18n.global.t(errorKey)
            : null;
    },

    // New Mutations for Room Details

    setRoomDetailsLoading(state, isLoading) {
        state.roomDetails.loading = isLoading;
    },
    setRoomDetailsData(state, data) {
        sessionStorage.removeItem("roomDetails"); // Jika disimpan di sessionStorage

        const normalized = normalizeRoomDetailsPayload(data);
        state.roomDetails.data = normalized;
        sessionStorage.setItem("roomDetails", JSON.stringify(normalized)); // Simpan ke sessionStorage
    },
    setRoomDetailsError(state, errorKey) {
        state.roomDetails.errorKey = errorKey;
        state.roomDetails.errorMessage = errorKey
            ? i18n.global.t(errorKey)
            : null;
    },

    // CategorySection
    setSelectedCategory(state, category) {
        state.selectedCategory = category;
    },
    setCategoryProperties(state, properties) {
        state.categoryProperties = Array.isArray(properties)
            ? properties.map(normalizeAvailabilityItem)
            : [];
    },
    setCategoryLoading(state, isLoading) {
        state.isCategoryLoading = isLoading;
    },

    //RecommedSection
    setRecommendedProperties(state, properties) {
        state.recommendedProperties = Array.isArray(properties)
            ? properties.map(normalizeAvailabilityItem)
            : [];
    },
    setRecommendedLoading(state, isLoading) {
        state.isRecommendedLoading = isLoading;
    },
    setFetchingRoomDetails(state, isFetching) {
        state.isFetchingRoomDetails = isFetching;
    },

    //Countries
    setCountries(state, countries) {
        state.countries = countries.map((country) => ({
            label: country.Name,
            value: country.Id,
        }));
    },
    setCountriesLoading(state, isLoading) {
        state.isCountriesLoading = isLoading;
    },
    setCountriesError(state, errorKey) {
        state.countriesError = errorKey;
    },

    //Provice
    setProvinces(state, provinces) {
        state.provinces = provinces.map((province) => ({
            label: province.Name,
            value: province.Id,
        }));
    },
    setProvincesLoading(state, isLoading) {
        state.isProvincesLoading = isLoading;
    },
    setProvincesError(state, errorKey) {
        state.provincesError = errorKey;
    },

    //Cities
    setCities(state, cities) {
        state.cities = cities.map((city) => ({
            label: city.Name,
            value: city.Id,
        }));
    },
    setCitiesLoading(state, isLoading) {
        state.isCitiesLoading = isLoading;
    },
    setCitiesError(state, errorKey) {
        state.citiesError = errorKey;
    },
    setOccupancyDateLoading(state, isLoading) {
        state.isOccupancyDateLoading = isLoading;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
